import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import { reportError, ErrorInfo } from 'app/shared/util/error-reporting';

interface IErrorBoundaryProps {
  readonly children?: JSX.Element | JSX.Element[];
  /**
   * Custom fallback UI.
   * - Pass a React node for a static fallback.
   * - Pass a render function `(reset: () => void) => ReactNode` to receive the
   *   boundary's reset callback so the fallback can offer a working Retry button.
   */
  readonly fallback?: React.ReactNode | ((reset: () => void) => React.ReactNode);
}

interface IErrorBoundaryState {
  readonly error: Error | undefined;
  readonly errorInfo: ErrorInfo | undefined;
  readonly hasError: boolean;
}

class ErrorBoundary extends React.Component<IErrorBoundaryProps, IErrorBoundaryState> {
  readonly state: IErrorBoundaryState = { error: undefined, errorInfo: undefined, hasError: false };

  static getDerivedStateFromError(error: Error): Partial<IErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    this.setState({ errorInfo: { componentStack: errorInfo.componentStack } });
    reportError(error, { componentStack: errorInfo.componentStack });
  }

  private handleRetry = (): void => {
    this.setState({ error: undefined, errorInfo: undefined, hasError: false });
  };

  render() {
    const { hasError, error, errorInfo } = this.state;
    if (hasError) {
      if (this.props.fallback) {
        const fallback = this.props.fallback;
        return <>{typeof fallback === 'function' ? fallback(this.handleRetry) : fallback}</>;
      }
      const isDev = process.env.NODE_ENV === 'development';
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100vh',
            gap: 2,
            textAlign: 'center',
            p: 4,
          }}
        >
          <Typography variant="h4" color="error" fontWeight="bold">
            Something went wrong
          </Typography>
          <Typography variant="body1" color="text.secondary">
            An unexpected error has occurred. Please try again or return to the home page.
          </Typography>
          {isDev && error && (
            <Box
              component="details"
              sx={{
                mt: 2,
                p: 2,
                bgcolor: 'grey.100',
                border: '1px solid',
                borderColor: 'grey.300',
                borderRadius: 1,
                maxWidth: 800,
                textAlign: 'left',
                fontSize: 12,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              <summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>Error details (dev only)</summary>
              <p>{error.toString()}</p>
              {errorInfo?.componentStack && <pre>{errorInfo.componentStack}</pre>}
            </Box>
          )}
          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <Button variant="contained" color="primary" onClick={this.handleRetry}>
              Retry
            </Button>
            <Button variant="outlined" onClick={() => (window.location.href = '/')}>
              Go Home
            </Button>
          </Box>
        </Box>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
